/**
 * Resource configuration for Dashboards overview page
 * Maps facetKeys to Typesense fields and defines value sources
 *
 * Feature-based visibility:
 * - Facets can be conditionally shown/hidden based on user's enabled features
 * - Use the `featureFlag` property in facet config to specify required feature
 *   (TLP features - not Posthog flags)
 * - AddFilterMenu component automatically filters facets based on user's features
 * - Features are fetched via React Query and cached across all components
 */

import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import i18n from 'i18next';

import { DASHBOARD_TYPES, SHARING_SCOPES } from './constants';
import type { TablePreferenceStorageKeys } from '@/hooks/grid';
import type { ResourceConfig } from '@/lib/resource-config';

export const dashboardsOverviewConfig = {
  pageIdentifier: 'DashboardsOverview',
  resourceKey: 'dashboardsOverview', // Matches i18n namespace
  typesenseCollection: 'dashboards',
  searchFields: ['Name', 'OwnerName'],
  facetSections: {
    dashboards: {
      get label() {
        return i18n.t('facetSections.Dashboards', { ns: 'dashboardsOverview' });
      },
    },
  },
  facets: {
    dashboardType: {
      section: 'dashboards',
      get label() {
        return i18n.t('facets.DashboardType', {
          ns: 'dashboardsOverview',
        });
      },
      field: 'DashboardType',
      type: 'string',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: [...DASHBOARD_TYPES],
      },
      facetBy: true, // Now we can enable faceting with readable names
      includeNullInNot: true,
    },
    author: {
      section: 'dashboards',
      get label() {
        return i18n.t('facets.Author', {
          ns: 'dashboardsOverview',
        });
      },
      field: 'OwnerId',
      type: 'int64',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'collection',
        collection: 'employees',
        idField: 'SourceId',
        slugField: 'SourceId', // Use ID for precise filtering
        labelField: 'Name',
        sortField: 'Name',
      },
      facetBy: true, // Now we can enable faceting with readable names
      includeNullInNot: true,
    },
    sharingScope: {
      section: 'dashboards',
      get label() {
        return i18n.t('facets.SharingScope', {
          ns: 'dashboardsOverview',
        });
      },
      field: 'SharingScope',
      type: 'string',
      operators: ['is', 'not'],
      valueSource: {
        kind: 'enum',
        values: [...SHARING_SCOPES],
      },
      facetBy: true, // Now we can enable faceting with readable names
      includeNullInNot: true,
    },
  },
} satisfies ResourceConfig;

/**
 * localStorage keys for DashboardsOverview preferences
 */
export const DASHBOARDS_OVERVIEW_STORAGE_KEYS = {
  // View mode
  viewMode: 'dashboards-overview-view-mode', // TODO needed?

  // Table view settings
  tableColumnVisibility: 'dashboards-overview-table-columns',
  tableColumnWidths: 'dashboards-overview-table-widths',
  tableColumnOrder: 'dashboards-overview-table-column-order',
  tableSort: 'dashboards-overview-table-sort',
  density: 'dashboards-overview-density',
} satisfies TablePreferenceStorageKeys;

/**
 * Default column visibility for DashboardsOverview table view
 */
export const defaultTableColumnVisibility: GridColumnVisibilityModel = {
  DashboardType: true,
  Name: true,
  OwnerId: true,
  CreatedAt: true,
  SharingScope: false,
};
